import { useLayoutEffect, useMemo, useState } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { CertificateRenderer } from '@/components/certificate/CertificateRenderer'
import { useCertificateSettings } from '@/services/super-admin/certificate-settings.service'
import { buildCertificatePreview, type CompanyCertificateView } from './certificate-preview'

/**
 * Pré-visualização do certificado de uma empresa, usado tanto pelo
 * super_admin quanto pelo admin local.
 *
 * Renderiza o MESMO componente que o aluno vê (`CertificateRenderer`),
 * com dados fake. A lógica de resolução (fallback empresa → global,
 * labels e avisos) vive em `certificate-preview.ts` — função pura,
 * testada à parte.
 *
 * `CompanyInput` é a forma estrutural mínima — qualquer objeto com os
 * campos relevantes (Company de `services/super-admin/companies.service`,
 * Company de `services/admin/companies.service`, ou um literal) serve.
 */
export interface CompanyInput extends CompanyCertificateView {
  /** Lista de badges configuradas pra exibir no preview. Cada item
   * tem `badge` com id/name/iconUrl (iconUrl null filtrado).
   * Ordem = ordem visual na faixa. Opcional. */
  certificateBadges?: Array<{
    order: number
    badge: { id: string; name: string; iconUrl: string | null }
  }>
}

// CertificateRenderer tem tamanho fixo 1240×877; com o wrapper interno
// (borda + padding p-4) o bloco natural fica ~1274×911. O modal escala
// esse bloco pra caber inteiro na viewport (largura E altura).
const RENDERER_W = 1274
const RENDERER_H = 911
// Espaço vertical reservado p/ header + badges + footer + paddings.
const DIALOG_CHROME = 280

interface CertificatePreviewDialogProps {
  company: CompanyInput | null
  onClose: () => void
}

export function CertificatePreviewDialog({ company, onClose }: CertificatePreviewDialogProps) {
  const { data: globalSettings, isLoading } = useCertificateSettings()

  const preview = useMemo(() => {
    if (!company) return null
    // Deriva a lista de badges no formato do CertificateRenderer: ordenadas,
    // filtradas pelas que têm iconUrl (consistente com o filtro do back —
    // no preview o "aluno fictício" conquistou tudo, então não há intersect).
    const badges = (company.certificateBadges ?? [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .filter((cb) => cb.badge.iconUrl)
      .map((cb) => ({
        id: cb.badge.id,
        name: cb.badge.name,
        iconUrl: cb.badge.iconUrl as string,
      }))
    return buildCertificatePreview(company, globalSettings, badges)
  }, [company, globalSettings])

  // Escala o certificado (tamanho fixo) pra caber inteiro no modal.
  // Calculada a partir da viewport — sem isso o overflow-auto interno
  // do CertificateRenderer cortava o certificado de 1240px.
  const [scale, setScale] = useState(0.7)
  useLayoutEffect(() => {
    if (!company) return
    const recompute = () => {
      const availW = window.innerWidth * 0.95 - 64
      const availH = window.innerHeight * 0.95 - DIALOG_CHROME
      const next = Math.min(availW / RENDERER_W, availH / RENDERER_H, 1)
      setScale(next > 0 ? next : 0.5)
    }
    recompute()
    window.addEventListener('resize', recompute)
    return () => window.removeEventListener('resize', recompute)
  }, [company])

  return (
    <Dialog open={!!company} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[95vw] max-w-[95vw] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pré-visualização do certificado — {company?.name}</DialogTitle>
          <DialogDescription>
            Como o certificado fica hoje com as configurações desta empresa. Os dados de aluno e
            curso são fictícios.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !preview ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="gap-1.5 font-normal">
                <span className="text-muted-foreground">Fundo:</span> {preview.bgLabel}
              </Badge>
              <Badge variant="secondary" className="gap-1.5 font-normal">
                <span className="text-muted-foreground">Assinatura:</span> {preview.sigLabel}
              </Badge>
            </div>

            {preview.warnings.length > 0 && (
              <div className="space-y-1.5">
                {preview.warnings.map((w) => (
                  <p key={w} className="flex items-start gap-1.5 text-xs text-amber-500">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {w}
                  </p>
                ))}
              </div>
            )}

            {/* Caixa externa com o tamanho JÁ escalado (elemento transformado
                não reserva layout); a interna tem a largura natural pra o
                overflow-auto do renderer não cortar o certificado. */}
            <div
              className="mx-auto"
              style={{ width: RENDERER_W * scale, height: RENDERER_H * scale }}
            >
              <div
                style={{
                  width: RENDERER_W,
                  transform: `scale(${scale})`,
                  transformOrigin: 'top left',
                }}
              >
                <CertificateRenderer data={preview.data} showDownload={false} />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
